// Facade over the v2 review-job + invitation APIs.
//
// Two flows live here:
//   - Review jobs   → POST /v2/requirements/projects/:id/reviews (marketplace)
//   - Invitations   → POST /v2/requirements/projects/:id/invitations (direct invite)
//
// Both flows have a localStorage shim so the UX keeps working when the
// backend is unreachable (guest mode, network blip, endpoint still in
// flight). The shim is idempotent on (projectId, target) and decays
// when the backend confirms the same record.

import RequirementsV2API from '../../../api/requirementsV2.js';
import { REVIEW_TIER_LEVEL_FLOOR } from '@moksy/common/src/permissions/levels.js';
import { formatMoney } from '../../../utils/currency';
import { reviewTier, reviewTierWord } from '@moksy/common/src/review/tiers.js';

const LOCAL_REVIEW_KEY = 'moksy_pending_review_intents_v1';
const LOCAL_INVITE_KEY = 'moksy_invites_v1';

// Per-project cap on active invites — prevents spam and gives a
// natural reason to nudge the user toward a paid tier instead of
// "send the link to everyone I know".
export const MAX_ACTIVE_INVITES_PER_PROJECT = 5;

// Mirrors InvitationService.ACTIVE_STATUSES on the server — pending, sent,
// opened, accepted — plus the local-only pending_invite. It used to omit
// `opened`, so the modal said "2 / 5 active" while the server counted five
// and refused the sixth.
const ACTIVE_INVITE_STATUSES = new Set(['pending_invite', 'pending', 'sent', 'opened', 'accepted']);

const readJsonArray = (key) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
};
const writeJsonArray = (key, list) => {
  try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* ignore */ }
};
const inviteId = () => `invite_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const ReviewStore = {
  // ── Marketplace review jobs ────────────────────────────────────────
  async listForProject(projectId) {
    if (!projectId) return [];
    try {
      const res = await RequirementsV2API.myReviews({ projectId });
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
      return items.filter((r) => !r.projectId || r.projectId === projectId);
    } catch (e) {
      const stashed = readJsonArray(LOCAL_REVIEW_KEY).filter((r) => r.projectId === projectId);
      if (stashed.length === 0) throw e;
      return stashed;
    }
  },

  async hasPendingForProject(projectId) {
    try {
      const list = await this.listForProject(projectId);
      return list.some((r) => ['pending', 'open', 'requested', 'assigned', 'in_progress'].includes(r.status));
    } catch {
      return false;
    }
  },

  /**
   * Return the first pending review object for a project (or null).
   * Distinct from hasPendingForProject because the modal needs to
   * branch on `tier` + `status` — a community-tier review that no
   * reviewer has claimed yet is upgradeable to a paid tier; a paid
   * one (or a claimed-and-in-progress community one) isn't.
   */
  async findPendingForProject(projectId) {
    try {
      const list = await this.listForProject(projectId);
      return list.find((r) =>
        ['pending', 'open', 'requested', 'assigned', 'in_progress'].includes(r.status)
      ) || null;
    } catch {
      return null;
    }
  },

  async create({ projectId, tier, scope = 'whole', sections = [], track = null, notes = '', requestKey = null }) {
    if (!projectId) throw new Error('projectId required');
    if (!['free', 'subscription', 'urgent'].includes(tier)) throw new Error('invalid tier');
    // 🧭 NO TRACK UNLESS THE CUSTOMER PICKED ONE.
    //
    // History, in two acts. This used to send `track: 'reviewer'`, which the
    // backend rejected (V2.REVIEW_INVALID_TRACK) so every submission silently
    // failed. The fix sent 'security' for every tier "to match the backend's
    // own default" — and that was the second bug: the modal has no track
    // picker, so every review on the platform became a security review, and
    // a reviewer accredited for education never saw an education project
    // (live: rj-836b468c, education-learning project, track security).
    //
    // The service now routes an absent track by the project's document
    // category (packages/common/src/permissions/tracks.js, trackForCategory),
    // and readAnswers returns the routed answer as `reviewTrack` so the
    // modal can say "Routed to Education reviewers". Only a track the
    // customer actually chose is sent; `null` means "route it".
    //
    // The level floors are the shared ones the admin approval page reads —
    // they were bare numbers here and nowhere else, so an operator could
    // grant a track at L5 for a queue that starts at L7 and nothing said so.
    const TIER_MAPPING = {
      // Prices are the review-tier table's floors (GC-058), not copies of them.
      free:         { requiredLevel: REVIEW_TIER_LEVEL_FLOOR.community,    priceMox: reviewTier('free').defaultMox,         track: track || null },
      subscription: { requiredLevel: REVIEW_TIER_LEVEL_FLOOR.subscription, priceMox: reviewTier('subscription').defaultMox, track: track || null },
      urgent:       { requiredLevel: REVIEW_TIER_LEVEL_FLOOR.urgent,       priceMox: reviewTier('urgent').defaultMox,       track: track || null }
    };
    const mapped = TIER_MAPPING[tier];
    const apiScope = scope === 'whole' || scope === 'whole_report' ? 'whole_report' : 'section';
    return RequirementsV2API.requestReview(projectId, {
      scope: apiScope,
      sections,
      track: mapped.track,
      requiredLevel: mapped.requiredLevel,
      priceMox: mapped.priceMox,
      // Forward the tier explicitly so the server knows whether to
      // hold escrow or skip it. The 'free' tier maps to a
      // community-pool review with no payment and no SLA; without
      // this flag the server bumps priceMox to the canonical floor
      // and fails free-tier requests with V2.MOX_INSUFFICIENT_BALANCE.
      tier,
      notes,
      // One key per request dialog: a double click or a retry returns the
      // same job instead of holding the escrow twice (JN-IDEMPOTENT).
      requestKey
    });
  },

  async cancel(jobId, { confirm = false } = {}) {
    if (!jobId) throw new Error('jobId required');
    return RequirementsV2API.cancelReview(jobId, { confirm });
  },

  // Reviewable through the cancel button. Pre-assignment (requested) is
  // a one-tap full refund. Post-assignment buckets still allow cancel
  // but the backend requires confirm:true after surfacing the 25/75
  // split policy — see V2.CANCEL_REQUIRES_CONFIRMATION handling on
  // MyReviewsPage.
  isCancellable(request) {
    if (!request) return false;
    return [
      'requested',
      'pending',
      'assigned',
      'in_progress',
      'submitted',
      'revision_requested'
    ].includes(request.status);
  },

  // Cancel mid-flight requires showing the user the policy split first.
  // Used by MyReviewsPage to decide whether to show a "Cancel" button
  // (clean) vs "Cancel & forfeit 25%" (policy split).
  requiresCancelConfirmation(request) {
    if (!request) return false;
    return ['assigned', 'in_progress', 'submitted', 'revision_requested'].includes(request.status);
  },

  // ── Personal invitations (direct-to-named-person) ──────────────────
  //
  // Each invite has its own id and lifecycle, distinct from a marketplace
  // ReviewJob. Backend endpoints (when present) at:
  //   POST  /v2/requirements/projects/:id/invitations
  //   GET   /v2/requirements/projects/:id/invitations
  //   POST  /v2/requirements/invitations/:id/resend
  //   POST  /v2/requirements/invitations/:id/cancel
  //
  // On any backend error we fall back to localStorage so the user
  // doesn't lose their typed message.
  async listInvitesForProject(projectId) {
    if (!projectId) return [];
    try {
      const res = await RequirementsV2API.listInvites(projectId);
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
      if (Array.isArray(items)) return items;
    } catch { /* fall through */ }
    return readJsonArray(LOCAL_INVITE_KEY).filter((i) => i.projectId === projectId);
  },

  async countActiveInvites(projectId) {
    const list = await this.listInvitesForProject(projectId);
    return list.filter((i) => ACTIVE_INVITE_STATUSES.has(i.status)).length;
  },

  async canSendInvite(projectId) {
    const active = await this.countActiveInvites(projectId);
    return {
      ok: active < MAX_ACTIVE_INVITES_PER_PROJECT,
      active,
      max: MAX_ACTIVE_INVITES_PER_PROJECT
    };
  },

  // The two write paths (backend + local stash) are split so the modal
  // can show "saved locally, sync pending" when the backend isn't
  // reachable. Both return the same record shape.
  async createInvite({ projectId, moksyUsername, name, email, note, sections = [], requestedBy = null }) {
    if (!projectId) throw new Error('projectId required');
    const cap = await this.canSendInvite(projectId);
    if (!cap.ok) {
      const err = new Error(`This project already has ${cap.max} active invites. Cancel or wait for one to clear before sending another.`);
      err.code = 'INVITE.CAP_EXCEEDED';
      throw err;
    }

    const payload = {
      moksyUsername: moksyUsername || null,
      name: name || null,
      email: email || null,
      note: note || '',
      sections,
      requestedBy: requestedBy || null
    };

    // Local stash first so the user gets immediate feedback even if
    // the backend rejects (auth, validation, endpoint missing).
    const local = {
      id: inviteId(),
      projectId,
      ...payload,
      status: 'pending_invite',
      createdAt: new Date().toISOString(),
      sentAt: null,
      backendDelivered: false
    };
    const stash = readJsonArray(LOCAL_INVITE_KEY);
    stash.unshift(local);
    writeJsonArray(LOCAL_INVITE_KEY, stash.slice(0, 100));

    try {
      const remote = await RequirementsV2API.createInvite(projectId, payload);
      const record = { ...local, ...remote, backendDelivered: true, status: remote?.status || 'sent' };
      // Replace the local row with the remote canonical version.
      const merged = [record, ...readJsonArray(LOCAL_INVITE_KEY).filter((i) => i.id !== local.id)];
      writeJsonArray(LOCAL_INVITE_KEY, merged.slice(0, 100));
      return record;
    } catch (e) {
      // A refusal is not an outage. When the server ANSWERED — cap reached,
      // already invited, inviting yourself, a colleague — the local stash is
      // dropped and the server's own reason is thrown for the modal to show.
      // The team walk had a customer told "we held it locally, we'll sync it
      // when the endpoint is reachable" for an invite the server had refused
      // by rule; they left believing it existed.
      const status = Number(e?.response?.status || 0);
      if (status >= 400 && status < 500) {
        writeJsonArray(LOCAL_INVITE_KEY, readJsonArray(LOCAL_INVITE_KEY).filter((i) => i.id !== local.id));
        const err = new Error(e?.response?.data?.message || e?.response?.data?.error?.message || 'The invitation was refused.');
        err.code = e?.response?.data?.code || e?.response?.data?.error?.code || 'INVITE.REFUSED';
        err.status = status;
        throw err;
      }
      // Genuinely unreachable (no response, or a 5xx): keep the stash so the
      // typed message survives, and say so.
      return { ...local, backendError: e?.response?.data?.error?.code || e?.response?.data?.message || e?.message || 'BACKEND_UNAVAILABLE' };
    }
  },

  async resendInvite(inviteRecordId) {
    if (!inviteRecordId) throw new Error('inviteId required');
    try {
      const remote = await RequirementsV2API.resendInvite(inviteRecordId);
      // Update local row if we have a mirror.
      const all = readJsonArray(LOCAL_INVITE_KEY);
      const idx = all.findIndex((i) => i.id === inviteRecordId);
      if (idx >= 0) { all[idx] = { ...all[idx], ...remote, status: remote?.status || 'sent', sentAt: new Date().toISOString() }; writeJsonArray(LOCAL_INVITE_KEY, all); }
      return remote;
    } catch (e) {
      const all = readJsonArray(LOCAL_INVITE_KEY);
      const idx = all.findIndex((i) => i.id === inviteRecordId);
      if (idx >= 0) { all[idx] = { ...all[idx], sentAt: new Date().toISOString() }; writeJsonArray(LOCAL_INVITE_KEY, all); }
      throw e;
    }
  },

  async cancelInvite(inviteRecordId) {
    if (!inviteRecordId) throw new Error('inviteId required');
    try { await RequirementsV2API.cancelInvite(inviteRecordId); } catch { /* still want to drop local */ }
    const all = readJsonArray(LOCAL_INVITE_KEY).map((i) =>
      i.id === inviteRecordId ? { ...i, status: 'cancelled', cancelledAt: new Date().toISOString() } : i
    );
    writeJsonArray(LOCAL_INVITE_KEY, all);
  },

  // ── Guest-intent stash (for register-then-resume flow) ─────────────
  stashGuestIntent({ projectId, tier, kind = 'review_request', ...rest }) {
    const list = readJsonArray(LOCAL_REVIEW_KEY).filter((r) => r.projectId !== projectId || r.kind !== kind);
    list.unshift({ projectId, tier, kind, stashedAt: new Date().toISOString(), ...rest });
    writeJsonArray(LOCAL_REVIEW_KEY, list.slice(0, 10));
  },
  readGuestIntent(projectId) {
    return readJsonArray(LOCAL_REVIEW_KEY).find((r) => r.projectId === projectId) || null;
  },
  clearGuestIntent(projectId) {
    writeJsonArray(LOCAL_REVIEW_KEY, readJsonArray(LOCAL_REVIEW_KEY).filter((r) => r.projectId !== projectId));
  }
};

// `money` is the shared rate hook's formatter ({ usd(mox), gbp(mox) }); pass
// it so the urgent tier's price is derived from its MOX amount at the current
// admin-set rate rather than a hardcoded number that drifts from it. Falls
// back to a $0.01/MOX standard when no formatter is given.
//
// The names and the urgent floor come from the one review-tier table
// (@moksy/common review/tiers.js, GC-058) rather than a copy kept here.
export const URGENT_PRICE_MOX = reviewTier('urgent').defaultMox;
export const getTiers = (money = null) => ([
  {
    id: 'free',
    label: reviewTierWord('free'),
    blurb: 'Free, open-time. Another member picks it up when they have time — usually within a few days, sometimes longer. No response window.',
    priceLabel: 'Free',
    badge: 'Free'
  },
  {
    id: 'subscription',
    label: reviewTierWord('subscription'),
    // ⚠️ 5, NOT 3. `SLA_TARGETS.subscription` in packages/common/src/sla is
    // `{ workingDays: 5, guaranteed: true }`, and that is the number the
    // clock is actually set from — a job claimed on Monday gets a deadline
    // the following Monday. This blurb promised three, so the product was
    // advertising a guarantee two working days tighter than the one it
    // enforces, on the one tier where the guarantee is the thing being sold.
    blurb: 'Included with your membership. An accredited reviewer reviews within 5 working days. Full report or a single section.',
    priceLabel: 'Included with plan',
    badge: 'Accredited'
  },
  {
    id: 'urgent',
    label: reviewTierWord('urgent'),
    // 36 hours is the lexicon's promise for a Priority Review; this blurb
    // said 24 and the picker's chip agreed with neither the policy nor
    // the lexicon.
    blurb: 'Pay-as-you-go. A senior accredited reviewer turns it around within 36 hours. Best when you have a meeting soon or a deadline you can\'t miss.',
    // 💱 THE PEG IS NOT THE CHARGE.
    //
    // This read "From $19.90" to a member whose plan, wallet and every invoice
    // are in pounds (walked 2026-09-10). MOX is pegged to the dollar and the
    // card is charged in £ — the wallet header says exactly that — so a lone
    // dollar figure tells the customer the peg and not what they will pay.
    // Billing already solves this for plan prices ("about £X — charged in
    // GBP"); the same pair is shown here, from the same rate hook.
    priceLabel: `From ${money ? `${money.usd(URGENT_PRICE_MOX)} (about ${money.gbp(URGENT_PRICE_MOX)})` : `$${(URGENT_PRICE_MOX * 0.01).toFixed(2)}`} · within 36 hours`,
    badge: 'Priority'
  }
]);

export const TIERS = [
  { id: 'free', label: reviewTierWord('free'), badge: 'Free' },
  { id: 'subscription', label: reviewTierWord('subscription'), badge: 'Accredited' },
  { id: 'urgent', label: reviewTierWord('urgent'), badge: 'Priority' }
];

export default ReviewStore;
